import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import { useUrgenti } from '../../../lib/dashboard-widgets-api';
import type { UrgentiItem } from '@produzionejb/types';
import { XlsxExportButton } from '../../../components/XlsxExportButton';

type Bucket = 'BARATTOLI' | 'SFUSI' | 'FUMI';
const BUCKET_ORDER: Bucket[] = ['BARATTOLI', 'SFUSI', 'FUMI'];

export function UrgentiWidget() {
  const { t } = useTranslation();
  const q = useUrgenti();

  const grouped = useMemo<Record<Bucket, UrgentiItem[]>>(() => {
    const out: Record<Bucket, UrgentiItem[]> = { BARATTOLI: [], SFUSI: [], FUMI: [] };
    for (const item of q.data?.items ?? []) {
      if (item.bucket && item.bucket !== 'OLI') {
        out[item.bucket as Bucket].push(item);
      }
    }
    return out;
  }, [q.data]);

  const totalCount = (q.data?.items ?? []).filter((i) => i.bucket && i.bucket !== 'OLI').length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle>{t('dashboard.urgenti.title')}</CardTitle>
        <XlsxExportButton
          path="/api/dashboard/urgenti?format=xlsx"
          fallbackFilename="urgenti.xlsx"
          label="Excel"
        />
      </CardHeader>
      <CardContent>
        {q.isLoading && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {q.isError && (
          <p className="text-sm text-red-600">{t('dashboard.urgenti.errorLoad')}</p>
        )}
        {!q.isLoading && !q.isError && totalCount === 0 && (
          <p className="text-sm text-slate-500">{t('dashboard.urgenti.empty')}</p>
        )}
        {totalCount > 0 && (
          <div className="space-y-3">
            {BUCKET_ORDER.map((b) =>
              grouped[b].length === 0 ? null : (
                <div key={b}>
                  <h4 className="mb-1 text-xs font-semibold uppercase text-slate-500">{b}</h4>
                  <table className="w-full text-xs">
                    <thead className="border-b border-slate-200 bg-slate-50">
                      <tr>
                        <th className="px-2 py-1 text-left font-medium text-slate-600">
                          {t('dashboard.urgenti.col.product')}
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          {t('dashboard.urgenti.col.grammatura')}
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          {t('dashboard.urgenti.col.stock')}
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          {t('dashboard.urgenti.col.openMss')}
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          {t('dashboard.urgenti.col.openMsf')}
                        </th>
                        <th className="px-2 py-1 text-right font-medium text-slate-600">
                          {t('dashboard.urgenti.col.deficit')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped[b].map((row) => (
                        <tr
                          key={`${row.productId}::${row.grammatura}`}
                          className="border-b border-slate-100 last:border-0"
                        >
                          <td className="px-2 py-1 text-slate-700">{row.productName}</td>
                          <td className="px-2 py-1 text-right font-mono">{row.grammatura}g</td>
                          <td className="px-2 py-1 text-right font-mono">{row.stockPezzi}</td>
                          <td className="px-2 py-1 text-right font-mono">{row.openOrdersMssPezzi}</td>
                          <td className="px-2 py-1 text-right font-mono">{row.openOrdersMsfPezzi}</td>
                          <td className="px-2 py-1 text-right font-mono font-semibold text-red-600">
                            {row.deficitPezzi}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ),
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
